/**
 * Retry/Backoff Mechanism for Background Syncs
 * 
 * Implements exponential backoff with jitter for graceful error handling
 */

export interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  jitterFactor?: number;
  onRetry?: (attempt: number, error: any, delay: number) => void;
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: any;
  attempts: number;
  totalTime: number;
}

/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<RetryResult<T>> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffMultiplier = 2,
    jitterFactor = 0.1,
    onRetry
  } = options;

  const startTime = Date.now();
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const data = await fn();
      return {
        success: true,
        data,
        attempts: attempt + 1,
        totalTime: Date.now() - startTime
      };
    } catch (error) {
      lastError = error;

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff and jitter
      const baseDelay = Math.min(initialDelay * Math.pow(backoffMultiplier, attempt), maxDelay);
      const jitter = baseDelay * jitterFactor * (Math.random() * 2 - 1);
      const delay = Math.max(0, baseDelay + jitter);

      // Notify about retry
      if (onRetry) {
        onRetry(attempt + 1, error, delay);
      }

      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${Math.round(delay)}ms:`, error);

      // Wait before next retry
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  return {
    success: false,
    error: lastError,
    attempts: maxRetries + 1,
    totalTime: Date.now() - startTime
  };
}

/**
 * Retry wrapper specifically for sync operations
 * Uses user-configured settings from database if available, falls back to defaults
 */
export async function retrySyncOperation<T>(
  operation: () => Promise<T>,
  operationName: string,
  userId?: string,
  onProgress?: (message: string) => void
): Promise<RetryResult<T>> {
  let maxRetries = 3;
  let initialDelay = 2000;
  let maxDelay = 10000;
  let backoffMultiplier = 2;

  // Try to load settings from database if userId provided
  if (userId && typeof window !== 'undefined') {
    try {
      const { createClient } = await import('@blinkdotnew/sdk');
      const blink = createClient({
        projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
        authRequired: false
      });

      const settings = await blink.db.userSyncSettings.list({
        where: { userId },
        limit: 1,
      });

      if (settings && settings.length > 0) {
        const s = settings[0];
        maxRetries = s.maxRetries || 3;
        initialDelay = s.initialDelay || 2000;
        maxDelay = s.maxDelay || 10000;
        backoffMultiplier = s.backoffMultiplier || 2;
      }
    } catch (error) {
      console.warn('Could not load sync settings from database, using defaults:', error);
    }
  }

  return retryWithBackoff(operation, {
    maxRetries,
    initialDelay,
    maxDelay,
    backoffMultiplier,
    jitterFactor: 0.2,
    onRetry: (attempt, error, delay) => {
      const message = `${operationName} failed, retrying in ${Math.round(delay / 1000)}s... (attempt ${attempt}/${maxRetries})`;
      console.log(message);
      if (onProgress) {
        onProgress(message);
      }
    }
  });
}

/**
 * Batch retry for multiple sync operations
 */
export async function retryBatchSync<T>(
  operations: Array<{
    name: string;
    fn: () => Promise<T>;
  }>,
  onProgress?: (operationName: string, status: 'running' | 'success' | 'failed', message?: string) => void
): Promise<Array<RetryResult<T>>> {
  const results: Array<RetryResult<T>> = [];

  for (const { name, fn } of operations) {
    if (onProgress) {
      onProgress(name, 'running');
    }

    const result = await retrySyncOperation(
      fn,
      name,
      (message) => {
        if (onProgress) {
          onProgress(name, 'running', message);
        }
      }
    );

    results.push(result);

    if (onProgress) {
      onProgress(
        name,
        result.success ? 'success' : 'failed',
        result.success ? 'Completed' : `Failed: ${result.error?.message || 'Unknown error'}`
      );
    }
  }

  return results;
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  // Network errors
  if (error.name === 'NetworkError' || error.message?.includes('network')) {
    return true;
  }

  // Rate limiting
  if (error.status === 429 || error.message?.includes('rate limit')) {
    return true;
  }

  // Temporary server errors
  if (error.status >= 500 && error.status < 600) {
    return true;
  }

  // Timeout errors
  if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
    return true;
  }

  return false;
}

/**
 * Create a retry-enabled fetch wrapper
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryOptions: RetryOptions = {}
): Promise<Response> {
  const result = await retryWithBackoff(
    async () => {
      const response = await fetch(url, options);
      
      // Throw on HTTP errors to trigger retry
      if (!response.ok) {
        const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.response = response;
        throw error;
      }
      
      return response;
    },
    {
      maxRetries: 3,
      initialDelay: 1000,
      maxDelay: 10000,
      ...retryOptions
    }
  );

  if (!result.success) {
    throw result.error;
  }

  return result.data!;
}
